/**
 * Promo code (coupon) rules. Pure, no Vue/Vuex/axios.
 *
 * Ported from the kiosk, adapted to the TableBee cart entry shape:
 * { item, quantity, size, extras } instead of a flat line.
 */

export const PROMO_TYPES = {
  BASKET_VALUE: "BASKET_VALUE",
  ITEM_QUANTITY: "ITEM_QUANTITY",
  ITEM_GROUP: "ITEM_GROUP",
  ITEM_BOOKING: "ITEM_BOOKING"
};

export const DISCOUNT_MODES = {
  PERCENTAGE: "PERCENTAGE",
  FIXED_AMOUNT: "FIXED_AMOUNT",
  FIXED_PRICE: "FIXED_PRICE"
};

export const PROMO_CONDITIONS = {
  OK: "OK",
  BASKET_TOO_LOW: "BASKET_TOO_LOW",
  NOT_ENOUGH_ITEMS: "NOT_ENOUGH_ITEMS",
  NO_ELIGIBLE_ITEM: "NO_ELIGIBLE_ITEM"
};

// backend marker, FE keeps detecting promos by promoRules
export const PROMO_EAN_PREFIX = "PROMO$";

export const round2 = n => Math.round((Number(n) || 0) * 100) / 100;

export const isPromoEntry = entry => !!entry && !!entry.promoRules;

export const findPromoEntry = entries =>
  (entries || []).find(isPromoEntry) || null;

/**
 * A promo line in an order that came back from the API. The order endpoint
 * resolves every position against the item table and the promo line carries the
 * code as its id, so it arrives with a null item and without its rules.
 */
export const isOrderedPromoPosition = position =>
  !!position && (isPromoEntry(position) || !position.item);

export const isBookingPromo = rules =>
  rules && rules.promotionType === PROMO_TYPES.ITEM_BOOKING;

// ITEM_GROUP / ITEM_BOOKING price the entries themselves, the rest deduct from the total.
export const isItemLevelPromo = rules =>
  (rules && rules.promotionType === PROMO_TYPES.ITEM_GROUP) ||
  isBookingPromo(rules);

export const promoBookingItemId = rules =>
  isBookingPromo(rules) && rules.itemIds ? rules.itemIds[0] : undefined;

/** requiredQuantity is a ceiling for a booking promo, not a threshold. */
export const promoBookingCap = rules =>
  Math.max(1, Number(rules && rules.requiredQuantity) || 1);

export const requiredTargetQuantity = rules =>
  isBookingPromo(rules)
    ? 1
    : Math.max(1, Number(rules && rules.requiredQuantity) || 0);

/* ---------- TableBee entry accessors ---------- */

const entryItem = entry => (entry && entry.item) || {};

/** Unit price the promo acts on: meal size wins over the item itself. */
export const entryBasePrice = entry => {
  if (!entry) return 0;
  if (entry.size && entry.size !== null)
    return Number(entry.size.finalizedPrice) || 0;
  return Number(entryItem(entry).finalizedPrice) || 0;
};

export const setEntryBasePrice = (entry, value) => {
  if (!entry) return;
  if (entry.size && entry.size !== null) entry.size.finalizedPrice = value;
  else entry.item.finalizedPrice = value;
};

export const entryExtrasTotal = entry => {
  if (!entry || !Array.isArray(entry.extras)) return 0;
  return entry.extras.reduce((sum, extra) => {
    const price = Number(extra.finalizedPrice) || 0;
    return extra.invertGarnishSelection
      ? sum - price
      : sum + price * (Number(extra.quantity) || 0);
  }, 0);
};

/** Money value of an entry, deposit excluded, mirroring the kiosk subtotal math. */
export const entryTotal = (entry, base = entryBasePrice(entry)) =>
  (Number(entry && entry.quantity) || 0) * (Number(base) || 0) +
  (Number(entry && entry.quantity) || 0) * entryExtrasTotal(entry);

export const entryItemId = entry => entryItem(entry).id;

export const entryItemGroupId = entry =>
  entryItem(entry).itemgroupId !== undefined
    ? entryItem(entry).itemgroupId
    : entryItem(entry).itemgroupID;

/* ---------- eligibility and conditions ---------- */

const isRealEntry = entry => !!entry && !isPromoEntry(entry);

export const realEntries = entries => (entries || []).filter(isRealEntry);

export function isEligibleEntry(entry, rules) {
  if (!isRealEntry(entry)) return false;
  if (rules && rules.promotionType === PROMO_TYPES.BASKET_VALUE) return true;
  if (!rules) return false;
  return (
    (rules.itemIds || []).includes(String(entryItemId(entry))) ||
    (rules.itemGroupIds || []).includes(String(entryItemGroupId(entry)))
  );
}

export const eligibleEntries = (entries, rules) =>
  (entries || []).filter(entry => isEligibleEntry(entry, rules));

export const basketSubtotal = entries =>
  round2(
    realEntries(entries).reduce((sum, entry) => sum + entryTotal(entry), 0)
  );

export const eligibleQuantity = (entries, rules) =>
  eligibleEntries(entries, rules).reduce(
    (sum, entry) => sum + (Number(entry.quantity) || 0),
    0
  );

/**
 * Units of itemId already in the cart against the cap of the redeemed booking
 * promo. null for every other item, nothing to enforce.
 */
export function promoBookingLimit(entries, rules, itemId, excludeIndex = null) {
  const bookedId = promoBookingItemId(rules);
  if (
    itemId === undefined ||
    bookedId === undefined ||
    String(bookedId) !== String(itemId)
  )
    return null;

  const used = (entries || []).reduce((sum, entry, index) => {
    if (String(entryItemId(entry)) !== String(itemId) || index === excludeIndex)
      return sum;
    return sum + (Number(entry.quantity) || 0);
  }, 0);

  return { cap: promoBookingCap(rules), used, code: rules.code };
}

/**
 * promotionType decides first: an ITEM_GROUP promo carries a minBasketValue in
 * the payload that must NOT be enforced.
 */
export function evaluatePromoConditions(entries, rules) {
  if (!rules) return { ok: false, reason: PROMO_CONDITIONS.NO_ELIGIBLE_ITEM };

  if (rules.promotionType === PROMO_TYPES.BASKET_VALUE) {
    const subtotal = basketSubtotal(entries);
    if (rules.minBasketValue > 0 && subtotal < rules.minBasketValue) {
      return {
        ok: false,
        reason: PROMO_CONDITIONS.BASKET_TOO_LOW,
        missingAmount: round2(rules.minBasketValue - subtotal)
      };
    }
    return { ok: true, reason: PROMO_CONDITIONS.OK };
  }

  if (rules.promotionType === PROMO_TYPES.ITEM_QUANTITY) {
    const qty = eligibleQuantity(entries, rules);
    const required = rules.requiredQuantity || 1;
    if (qty < required) {
      return {
        ok: false,
        reason: PROMO_CONDITIONS.NOT_ENOUGH_ITEMS,
        missingQuantity: required - qty
      };
    }
    return { ok: true, reason: PROMO_CONDITIONS.OK };
  }

  if (eligibleEntries(entries, rules).length < 1) {
    return { ok: false, reason: PROMO_CONDITIONS.NO_ELIGIBLE_ITEM };
  }
  return { ok: true, reason: PROMO_CONDITIONS.OK };
}

/* ---------- pricing ---------- */

/** Order-level reduction, never bigger than the cart it discounts. */
export function computePromoDiscount(base, rules) {
  if (!rules || base <= 0) return 0;
  const raw =
    rules.discountMode === DISCOUNT_MODES.PERCENTAGE
      ? base * (rules.discountValue / 100)
      : rules.discountValue;
  return round2(Math.min(Math.max(raw, 0), base));
}

/** Item-level promos keep the promo entry at 0, the reduction sits on the entries. */
export function promoLinePrice(entries, rules) {
  if (!rules || isItemLevelPromo(rules)) return 0;
  const discount = computePromoDiscount(basketSubtotal(entries), rules);
  return discount > 0 ? -discount : 0;
}

/** Discounted unit price for an eligible entry. Paid extras still stack on top. */
export function entryPromoBasePrice(entry, rules, originalBase) {
  const base =
    originalBase !== undefined
      ? Number(originalBase) || 0
      : entryBasePrice(entry);

  if (rules.discountMode === DISCOUNT_MODES.FIXED_PRICE) {
    return Math.max(0, round2(rules.discountValue));
  }
  if (rules.discountMode === DISCOUNT_MODES.PERCENTAGE) {
    return round2(base * ((100 - rules.discountValue) / 100));
  }
  return Math.max(0, round2(base - rules.discountValue));
}

/** True while an item level promo is pricing this entry. */
export const isPromoDiscounted = entry =>
  !!entry && entry.promoOriginalBase !== undefined;

/** What the entry cost before the promo touched it. */
export const promoOriginalTotal = entry =>
  isPromoDiscounted(entry)
    ? round2(entryTotal(entry, entry.promoOriginalBase))
    : 0;

export const promoOriginalUnitPrice = entry =>
  isPromoDiscounted(entry) ? round2(Number(entry.promoOriginalBase) || 0) : 0;

/** Whole percent saved on this entry, for the badge. */
export function promoDiscountPercent(entry) {
  if (!isPromoDiscounted(entry)) return 0;

  const before = entryTotal(entry, entry.promoOriginalBase);
  if (before <= 0) return 0;

  return Math.round(((before - entryTotal(entry)) / before) * 100);
}

/** Total saved across the entries an item-level promo actually touched. */
export function itemPromoSavings(entries) {
  return round2(
    (entries || []).reduce((sum, entry) => {
      if (!entry || entry.promoOriginalBase === undefined) return sum;
      return (
        sum + (entryTotal(entry, entry.promoOriginalBase) - entryTotal(entry))
      );
    }, 0)
  );
}

/* ---------- cart entry ---------- */

export function buildPromoCartEntry(rules, price = 0) {
  return {
    item: {
      id: rules.code,
      ean: PROMO_EAN_PREFIX + rules.code,
      name: rules.name || rules.code,
      description: rules.description || "",
      sellingPrice: price,
      price: price,
      finalizedPrice: price,
      taxedPrice: price,
      tax: null,
      extras: [],
      sizes: [],
      allergens: [],
      deposit: null,
      image: "",
      imagePreview: ""
    },
    quantity: 1,
    size: null,
    extras: [],
    promoRules: rules,
    promoSavings: 0
  };
}

/** Drops the hint-only target names, the order endpoint has no use for them. */
export function promoEntryForOrder(entry) {
  if (!isPromoEntry(entry)) return entry;
  const promoRules = { ...entry.promoRules };
  delete promoRules.itemNames;
  delete promoRules.itemGroupNames;
  return { ...entry, promoRules };
}

const setPromoEntryPrice = (promo, price) => {
  promo.item.sellingPrice = price;
  promo.item.price = price;
  promo.item.finalizedPrice = price;
  promo.item.taxedPrice = price;
};

/**
 * The cart as the order endpoint has to receive it.
 *
 * An item level promo re-prices its entries in the cart, but the order endpoint
 * prices every position from the item table and drops whatever the client sent,
 * so that discount would be lost. Book it the way a BASKET_VALUE promo is
 * booked instead: entries at their catalogue price and the whole discount on the
 * promo line. The cart keeps its own per row prices, only the payload changes.
 */
export function cartForOrder(entries) {
  const payload = JSON.parse(JSON.stringify(entries || []));
  const promo = findPromoEntry(payload);
  const savings = itemPromoSavings(payload);

  payload.forEach(entry => {
    if (!entry || entry.promoOriginalBase === undefined) return;
    setEntryBasePrice(entry, entry.promoOriginalBase);
    delete entry.promoOriginalBase;
  });

  // promoSavings means "already taken off the item prices", which is no longer
  // the case once the discount sits on the promo line: keep them from being
  // counted twice
  if (promo && savings > 0) {
    setPromoEntryPrice(promo, -savings);
    promo.promoSavings = 0;
  }

  return payload.map(promoEntryForOrder);
}

/* ---------- response mapping ---------- */

/** The backend flags success inconsistently across the promo endpoints. */
export function isPromoSuccess(data) {
  if (!data) return false;
  const flag = data.success !== undefined ? data.success : data.status;
  if (flag === true || flag === 1 || flag === "1") return true;
  if (typeof flag === "string") return flag.toUpperCase() === "SUCCESS";
  return false;
}

export const promoMessage = data =>
  (data && (data.msg || data.message)) || null;

// Targets arrive as [{ id, name }], older payloads as bare ids.
const toTargets = list =>
  (Array.isArray(list) ? list : []).map(entry =>
    entry && typeof entry === "object"
      ? { id: String(entry.id), name: entry.name || "" }
      : { id: String(entry), name: "" }
  );

export function promoRulesFromResponse(promotion, code) {
  if (!promotion) return null;
  const itemTargets = toTargets(promotion.itemIds);
  const groupTargets = toTargets(promotion.itemGroupIds);
  return {
    code: String(promotion.code || code || "")
      .trim()
      .toUpperCase(),
    name: promotion.name || promotion.code || code,
    description: promotion.description || "",
    promotionType: promotion.promotionType,
    discountMode: promotion.discountMode,
    discountValue: Number(promotion.discountValue) || 0,
    minBasketValue: Number(promotion.minBasketValue) || 0,
    requiredQuantity: Number(promotion.requiredQuantity) || 0,
    itemGroupIds: groupTargets.map(target => target.id),
    itemIds: itemTargets.map(target => target.id),
    itemNames: itemTargets.map(target => target.name).filter(Boolean),
    itemGroupNames: groupTargets.map(target => target.name).filter(Boolean)
  };
}

/** Promo target items out of a flat item array or a category shaped array. */
export function promoTargetsFromResponse(data, rules) {
  const entries =
    [
      data && data.items,
      data && data.promotion && data.promotion.items,
      data && data.promotion && data.promotion.itemGroups
    ].find(entry => Array.isArray(entry) && entry.length) || [];

  const wanted = new Set(((rules && rules.itemIds) || []).map(String));

  return entries.flatMap(entry =>
    Array.isArray(entry.items)
      ? entry.items
          .filter(item => wanted.has(String(item.id)))
          .map(item => ({
            ...item,
            itemgroupId: entry.id !== undefined ? entry.id : item.itemgroupId
          }))
      : wanted.has(String(entry.id))
      ? [{ ...entry }]
      : []
  );
}

/** Mirrors the booking flow: anything that opens the product details screen. */
export function targetNeedsDetails(item) {
  if (!item) return false;
  if ((item.sizes || []).length > 0) return true;
  if (Array.isArray(item.extras) ? item.extras.length > 0 : !!item.extras)
    return true;
  return false;
}
